import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, MenuItem, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tabs, Tab, TextField, Typography,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { getData } from "../../apis/api";
import { ENDPOINTS } from "../../apis/urls";

const statuses = ["active", "waiting_delay", "waiting_callback", "waiting_event", "paused", "completed", "failed", "cancelled", "suppressed"];
const date = (value) => value ? new Date(value).toLocaleString() : "—";
const label = (value) => (value || "unknown").replace(/_/g, " ");
const badge = (value) => <Chip size="small" label={label(value)} color={value === "failed" ? "error" : ["completed", "success"].includes(value) ? "success" : "default"} />;
const errorMessage = (error) => typeof error?.message === "string" ? error.message : "Unable to load workflow activity. Please try again.";

export default function InsightsModal({ open, onClose, workflowId, darkMode }) {
  const theme = useMemo(() => createTheme({ palette: { mode: darkMode ? "dark" : "light" } }), [darkMode]);
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [journey, setJourney] = useState(null);
  const [journeyLoading, setJourneyLoading] = useState(false);
  const [journeyError, setJourneyError] = useState("");

  useEffect(() => {
    setSelected(null);
    setPage(1);
    setSearch("");
    setStatus("");
  }, [workflowId, open]);

  useEffect(() => {
    if (!open || !workflowId) return;
    let live = true;
    setLoading(true);
    setError("");
    const timer = setTimeout(async () => {
      try {
        if (tab === 0) {
          const result = await getData(ENDPOINTS.WORKFLOW.ENROLLMENTS, { workflowId, page, pageSize: 25, search, status });
          if (live) { setRows(result.Content || []); setPagination(result.pagination); }
        } else {
          const result = await getData(ENDPOINTS.WORKFLOW.ANALYTICS(workflowId));
          if (live) setMetrics(result.Content);
        }
      } catch (err) { if (live) setError(errorMessage(err)); }
      finally { if (live) setLoading(false); }
    }, 250);
    return () => { live = false; clearTimeout(timer); };
  }, [open, workflowId, tab, page, search, status, refresh]);

  useEffect(() => {
    if (!open || !selected) return;
    let live = true;
    setJourney(null);
    setJourneyError("");
    setJourneyLoading(true);
    getData(ENDPOINTS.WORKFLOW.ENROLLMENT_TIMELINE(selected.id))
      .then((result) => { if (live) setJourney(result); })
      .catch((err) => { if (live) setJourneyError(errorMessage(err)); })
      .finally(() => { if (live) setJourneyLoading(false); });
    return () => { live = false; };
  }, [open, selected, refresh]);

  const enrollment = journey?.enrollment || selected;
  return <ThemeProvider theme={theme}><Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { minHeight: "70vh" } }}>
    <DialogTitle>Contacts & metrics</DialogTitle>
    <DialogContent dividers>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Tabs value={tab} onChange={(_, value) => { setTab(value); setSelected(null); }} aria-label="Workflow activity">
          <Tab label="Enrolled contacts" /><Tab label="Metrics" />
        </Tabs>
        <Button onClick={() => setRefresh((value) => value + 1)}>Refresh</Button>
      </Stack>
      {tab === 0 && !selected && <>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2}>
          <TextField size="small" label="Search name or phone" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} />
          <TextField select size="small" label="Status" value={status} sx={{ minWidth: 200 }} onChange={(event) => { setStatus(event.target.value); setPage(1); }}>
            <MenuItem value="">All statuses</MenuItem>{statuses.map((item) => <MenuItem key={item} value={item}>{label(item)}</MenuItem>)}
          </TextField>
        </Stack>
        {error ? <Alert severity="error">{error}</Alert> : loading ? <CircularProgress aria-label="Loading enrollments" /> : <>
          <Typography variant="body2" mb={1}>{pagination?.total ?? rows.length} enrollments · Each enrollment has its own journey, including repeat enrollments.</Typography>
          <TableContainer><Table size="small" aria-label="Enrolled contacts"><TableHead><TableRow>
            {["Contact", "Status", "Current step", "Source", "Enrolled", "Journey"].map((heading) => <TableCell key={heading}>{heading}</TableCell>)}
          </TableRow></TableHead><TableBody>
            {rows.map((row) => <TableRow key={row.id}>
              <TableCell><Typography>{row.contactName || (row.contact ? `Contact #${row.contact}` : "Contact unavailable")}</Typography><Typography variant="caption">{row.contactPhone || "No phone"} · Enrollment #{row.id} · v{row.version}</Typography></TableCell>
              <TableCell>{badge(row.status)}</TableCell><TableCell>{row.currentNodeKey || "—"}</TableCell>
              <TableCell>{label(row.source)}</TableCell><TableCell>{date(row.created_at)}</TableCell>
              <TableCell><Button size="small" onClick={() => setSelected(row)}>View journey</Button></TableCell>
            </TableRow>)}
            {!rows.length && <TableRow><TableCell colSpan={6}>No enrollments match these filters.</TableCell></TableRow>}
          </TableBody></Table></TableContainer>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end" mt={2}>
            <Button disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</Button>
            <Typography>Page {page} of {Math.max(1, pagination?.totalPages || 1)}</Typography>
            <Button disabled={!pagination?.hasNext} onClick={() => setPage(page + 1)}>Next</Button>
          </Stack>
        </>}
      </>}
      {tab === 0 && selected && <>
        <Button onClick={() => setSelected(null)} sx={{ mb: 2 }}>Back to contacts</Button>
        <Typography variant="h6">{enrollment.contactName || `Enrollment #${enrollment.id}`}</Typography>
        <Stack direction="row" spacing={1} alignItems="center" my={1}>{badge(enrollment.status)}<Typography variant="body2">Enrollment #{enrollment.id} · Version {enrollment.version} · {label(enrollment.source)}</Typography></Stack>
        {journeyLoading ? <CircularProgress aria-label="Loading journey" /> : journeyError ? <Alert severity="error">{journeyError}</Alert> : journey && <>
          <Typography variant="body2">Enrolled {date(enrollment.created_at)} · Started {date(enrollment.startedAt)} · Finished {date(enrollment.completedAt)}</Typography>
          <Typography variant="body2" mb={2}>Current step: {journey.currentNode?.label || enrollment.currentNodeKey || "—"}{enrollment.completionReason ? ` · ${label(enrollment.completionReason)}` : ""}</Typography>
          {enrollment.lastError && <Alert severity="error" sx={{ mb: 2 }}>{enrollment.lastError}</Alert>}
          <Typography variant="subtitle1">Execution log ({journey.Content.length} recorded steps)</Typography>
          {!journey.Content.length && <Typography sx={{ my: 2 }}>No steps have executed yet.</Typography>}
          <Stack component="ol" spacing={2} sx={{ pl: 3 }}>
            {journey.Content.map((run) => <Paper component="li" variant="outlined" key={run.id} sx={{ p: 2 }}>
              <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap"><Typography fontWeight={600}>{run.nodeLabel || run.nodeType} ({run.nodeKey})</Typography>{badge(run.outcome)}</Stack>
              <Typography variant="caption">{date(run.created_at)} · Attempt {run.attempt} · {run.durationMs ?? "—"} ms · Log #{run.id}</Typography>
              {run.branchTaken && <Typography variant="body2">Branch: {run.branchTaken}{run.nextNodeKey ? ` → ${run.nextNodeKey}` : ""}</Typography>}
              {run.externalRef && <Typography variant="body2">External reference: {run.externalRef}</Typography>}
              {run.errorMessage && <Alert severity="error" sx={{ my: 1 }}>{run.errorMessage}</Alert>}
              <Box component="details" mt={1}><summary>Input and output</summary><Box component="pre" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", fontSize: 12 }}>{JSON.stringify({ input: run.inputSnapshot, output: run.outputSnapshot }, null, 2)}</Box></Box>
            </Paper>)}
          </Stack>
          <Typography variant="subtitle1" mt={3}>Task history & scheduled work</Typography>
          <TableContainer><Table size="small" aria-label="Journey tasks"><TableHead><TableRow>{["Step", "Status", "Scheduled for", "Attempts", "Error"].map((item) => <TableCell key={item}>{item}</TableCell>)}</TableRow></TableHead><TableBody>
            {(journey.tasks || []).map((task) => <TableRow key={task.id}><TableCell>{task.nodeKey}</TableCell><TableCell>{badge(task.status)}</TableCell><TableCell>{date(task.runAfter)}</TableCell><TableCell>{task.attempts} / {task.maxAttempts}</TableCell><TableCell>{task.lastError || "—"}</TableCell></TableRow>)}
            {!journey.tasks?.length && <TableRow><TableCell colSpan={5}>No tasks recorded.</TableCell></TableRow>}
          </TableBody></Table></TableContainer>
        </>}
      </>}
      {tab === 1 && (error ? <Alert severity="error">{error}</Alert> : loading ? <CircularProgress aria-label="Loading metrics" /> : metrics && <>
        <Typography variant="body2" mb={2}>All-time metrics across every published version. Rates use all enrollments as the denominator; execution counts include retries.</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 2, mb: 3 }}>
          {[["Enrollments", metrics.totalEnrollments], ["Unique contacts", metrics.uniqueContacts], ["Completion rate", `${metrics.completionRate}%`], ["Failure rate", `${metrics.failureRate}%`], ["Step executions", metrics.totalStepRuns], ["Avg. step duration", `${Math.round(metrics.avgStepDurationMs || 0)} ms`]].map(([title, value]) => <Paper variant="outlined" key={title} sx={{ p: 2 }}><Typography variant="body2">{title}</Typography><Typography variant="h5">{value ?? 0}</Typography></Paper>)}
        </Box>
        <Typography variant="subtitle1">Enrollment status</Typography>
        <Stack direction="row" gap={1} flexWrap="wrap" my={2}>{statuses.map((item) => <Chip key={item} label={`${label(item)}: ${metrics.statusCounts?.[item] || 0}`} />)}</Stack>
        <Typography variant="subtitle1">Execution outcomes</Typography>
        <Stack direction="row" gap={1} flexWrap="wrap" my={2}>{Object.entries(metrics.outcomeCounts || {}).map(([key, count]) => <Chip key={key} label={`${label(key)}: ${count}`} />)}</Stack>
        <Typography variant="subtitle1">Step performance</Typography>
        <TableContainer><Table size="small" aria-label="Step performance"><TableHead><TableRow>{["Version / step", "Type", "Executions", "Successful", "Failed", "Waiting", "Avg. duration"].map((item) => <TableCell key={item}>{item}</TableCell>)}</TableRow></TableHead><TableBody>
          {(metrics.nodeMetrics || []).map((node) => <TableRow key={`${node.version}:${node.nodeKey}:${node.nodeType}`}><TableCell>v{node.version} / {node.nodeKey}</TableCell><TableCell>{label(node.nodeType)}</TableCell><TableCell>{node.totalRuns}</TableCell><TableCell>{node.successfulRuns}</TableCell><TableCell>{node.failedRuns}</TableCell><TableCell>{node.waitingRuns}</TableCell><TableCell>{node.avgDurationMs == null ? "—" : `${Math.round(node.avgDurationMs)} ms`}</TableCell></TableRow>)}
          {!metrics.nodeMetrics?.length && <TableRow><TableCell colSpan={7}>No steps have executed yet.</TableCell></TableRow>}
        </TableBody></Table></TableContainer>
        <Typography variant="subtitle1" mt={3}>Branches taken</Typography>
        <Stack direction="row" gap={1} flexWrap="wrap" my={2}>{Object.entries(metrics.branchesTaken || {}).map(([key, count]) => <Chip key={key} label={`${key}: ${count}`} />)}</Stack>
      </>)}
    </DialogContent>
    <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
  </Dialog></ThemeProvider>;
}
